/** DTO types shared between the ZenPort server API and the web client. */
// --- Content types ---
/**
 * What a library item is for. Two practise, two teach:
 *  - meditation: a guided practice (intro tracks included)
 *  - soundscape: music, sound baths, ambient or sleep sound
 *  - course:     a series of lessons worked through in order
 *  - talk:       a single lecture, livestream, workshop or Q&A
 */
export const CONTENT_TYPES = ['meditation', 'course', 'talk', 'soundscape'];
/** Types that count as practice (streaks, practice minutes); the rest are learning. */
export const PRACTICE_TYPES = ['meditation', 'soundscape'];
export const isPracticeType = (t) => PRACTICE_TYPES.includes(t);
/** Containers a browser plays as video. (mpg/flv are not playable and never indexed.) */
export const VIDEO_EXTS = ['mp4', 'm4v', 'webm', 'mov'];
export const isVideoExt = (ext) => VIDEO_EXTS.includes(ext.toLowerCase());
/** Beginner first; "every level" sits with beginners, unknown in the middle. */
export const LEVEL_ORDER = {
    beginner: 0,
    all: 1,
    intermediate: 2,
    advanced: 3,
};
/** Minutes a meditation's place is kept for an accidental exit; courses keep theirs. */
export const PRACTICE_RESUME_MINUTES = 10;
// --- Practice sessions ---
/**
 * Reserved meditation id for an unguided sit on the timer, which has no
 * library item behind it. Real item ids are 20-char hex digests, so this can
 * never collide with one. The server recognises it and skips the items
 * lookup; the API renders it as "Unguided sit" rather than as a removed item.
 */
export const TIMER_ITEM_ID = 'zenport:timer';
export const TIMER_ITEM_TITLE = 'Unguided sit';
/** Where a date the cadence produced lands after the plan's pushes. */
export function shiftedDate(date, shifts) {
    let d = date;
    for (const s of shifts) {
        if (d >= s.from) {
            d = new Date(new Date(`${d}T00:00:00Z`).getTime() + s.days * 86_400_000)
                .toISOString()
                .slice(0, 10);
        }
    }
    return d;
}
/** A series starred as a whole: its creator and name, as one key. */
export const seriesFavoriteKey = (creator, series) => `series:${JSON.stringify([creator, series])}`;
/** The creator and series a series key names, or null for a recording's id. */
export function parseSeriesFavoriteKey(key) {
    if (!key.startsWith('series:'))
        return null;
    try {
        const v = JSON.parse(key.slice(7));
        return Array.isArray(v) && v.length === 2 && v.every((x) => typeof x === 'string')
            ? [v[0], v[1]]
            : null;
    }
    catch {
        return null;
    }
}
export const AI_PROVIDERS = [
    {
        id: 'openai',
        label: 'OpenAI',
        blurb: 'GPT models, from the makers of ChatGPT.',
        needsKey: true,
        needsBaseUrl: false,
        webSearch: true,
        adminOnly: false,
        keyUrl: 'https://platform.openai.com/api-keys',
    },
    {
        id: 'anthropic',
        label: 'Anthropic',
        blurb: 'Claude - careful, thoughtful writing.',
        needsKey: true,
        needsBaseUrl: false,
        webSearch: true,
        adminOnly: false,
        keyUrl: 'https://console.anthropic.com/settings/keys',
    },
    {
        id: 'gemini',
        label: 'Google Gemini',
        blurb: "Google's Gemini models, with Google Search.",
        needsKey: true,
        needsBaseUrl: false,
        webSearch: true,
        adminOnly: false,
        keyUrl: 'https://aistudio.google.com/apikey',
    },
    {
        id: 'openrouter',
        label: 'OpenRouter',
        blurb: 'One key for hundreds of models from many makers.',
        needsKey: true,
        needsBaseUrl: false,
        webSearch: true,
        adminOnly: false,
        keyUrl: 'https://openrouter.ai/keys',
    },
    {
        id: 'compatible',
        label: 'Your own AI server',
        blurb: 'Ollama, LM Studio or any OpenAI-compatible server - nothing leaves your network.',
        needsKey: false,
        needsBaseUrl: true,
        webSearch: false,
        adminOnly: true,
        keyUrl: null,
    },
];
/** Personal AI features a shared key can be opened or closed for. */
export const AI_FEATURES = [
    { id: 'plan', label: 'Plan with AI' },
    { id: 'guide', label: 'Your guide' },
    { id: 'featured', label: 'For you today' },
    { id: 'discover', label: 'Discover' },
    { id: 'sits', label: 'Made for you' },
];
// --- Intentions: why someone practises, for everything the AI does ---
export const INTENTION_REASONS = [
    { id: 'calm', label: 'Calm and less stress' },
    { id: 'sleep', label: 'Better sleep' },
    { id: 'focus', label: 'Focus and clarity' },
    { id: 'emotions', label: 'Working with emotions' },
    { id: 'healing', label: 'Healing' },
    { id: 'self', label: 'Knowing myself' },
    { id: 'spirit', label: 'Spiritual growth' },
    { id: 'health', label: 'Body and health' },
    { id: 'kindness', label: 'Kindness and connection' },
    { id: 'curious', label: 'Curiosity' },
];
export const INTENTION_LIKES = [
    { id: 'guided', label: 'Guided meditations' },
    { id: 'silent', label: 'Silent sits' },
    { id: 'breath', label: 'Breathwork' },
    { id: 'body', label: 'Body scans' },
    { id: 'courses', label: 'Courses and lessons' },
    { id: 'talks', label: 'Talks and lectures' },
    { id: 'sound', label: 'Sound and music' },
    { id: 'visualise', label: 'Visualisation' },
];
// ── The guide (AI mentor) ─────────────────────────────────────────────────
export const GUIDE_PERIODS = [7, 30, 90];
// ── Discover (AI, web search) ─────────────────────────────────────────────
export const DISCOVER_KINDS = [
    { id: 'teacher', label: 'Teachers' },
    { id: 'course', label: 'Courses' },
    { id: 'book', label: 'Books' },
    { id: 'retreat', label: 'Retreats & workshops' },
];
// ── Made for you: a guided meditation written and spoken for this moment ──
/** Practice sessions of a made-for-you sit use `ai:<id>` as their item. */
export const SIT_ITEM_PREFIX = 'ai:';
export const SIT_TITLE = 'Made for you';
export const SIT_FEELINGS = [
    { id: 'restless', label: 'Restless' },
    { id: 'anxious', label: 'Anxious' },
    { id: 'tired', label: 'Tired' },
    { id: 'scattered', label: 'Scattered' },
    { id: 'low', label: 'Low' },
    { id: 'tender', label: 'Tender' },
    { id: 'sleepless', label: 'Can’t sleep' },
    { id: 'grateful', label: 'Grateful' },
    { id: 'calm', label: 'Calm' },
];
export const SIT_FOCI = [
    { id: 'any', label: 'Let it choose' },
    { id: 'breath', label: 'Breath' },
    { id: 'body', label: 'Body' },
    { id: 'kindness', label: 'Kindness' },
    { id: 'awareness', label: 'Open awareness' },
    { id: 'sleep', label: 'Sleep' },
];
export const SIT_VOICES = [
    { id: 'sage', label: 'Sage', hint: 'Soft and clear' },
    { id: 'coral', label: 'Coral', hint: 'Warm' },
    { id: 'ballad', label: 'Ballad', hint: 'Gentle' },
    { id: 'ash', label: 'Ash', hint: 'Low and steady' },
];
export const SIT_LENGTHS = [5, 10, 15, 20];
//# sourceMappingURL=types.js.map